import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Building2, User, ChevronRight, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApolloClient } from "@apollo/client/react";
import OrganizationOnboarding from "@/components/onboarding/OrganizationOnboarding";
import IndividualOnboarding from "@/components/onboarding/IndividualOnboarding";

type AccountType = "organization" | "individual" | null;

const Onboarding = () => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState<AccountType>(null);
  const [onboardingData, setOnboardingData] = useState<Record<string, unknown> | null>(null);

  // Check for auth token on mount - redirect to login if not found
  useEffect(() => {
    const googleIdToken = sessionStorage.getItem('pendingGoogleIdToken');
    const emailSignup = sessionStorage.getItem('pendingEmailSignup');
    if (!googleIdToken && !emailSignup) {
      console.warn("No auth token found in session, redirecting to login");
      navigate("/login");
    }
  }, [navigate]);

  const totalSteps = accountType === "organization" ? 3 : 3;
  const progress = (step / totalSteps) * 100;

  const handleAccountTypeSelection = (type: AccountType) => {
    setAccountType(type);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      setAccountType(null);
      setStep(1);
    } else {
      setStep(step - 1);
    }
  };

  const handleComplete = (data: unknown) => {
    // Store onboarding data to pass to completion handler
    setOnboardingData(data as Record<string, unknown>);
    // The actual completion will be handled by the child components
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 py-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 animate-fade-in">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-hero flex items-center justify-center shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Welcome to FlowLabs
            </h1>
            <p className="text-muted-foreground">
              Let's set up your account in a few simple steps
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Step {step} of {totalSteps}</span>
            <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Content */}
        <div className="animate-fade-in">
          {step === 1 && (
            <Card className="p-8 border-border shadow-xl">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold">Choose Your Account Type</h2>
                  <p className="text-muted-foreground">
                    Select the option that best describes how you'll use FlowLabs
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {/* Individual Card */}
                  <Card
                    className="p-8 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 border-2 border-border group"
                    onClick={() => handleAccountTypeSelection("individual")}
                  >
                    <div className="space-y-6">
                      <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <User className="w-8 h-8 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold">Individual</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Great for solo creators, freelancers, and personal projects. 
                          Simple setup and easy to use.
                        </p>
                      </div>
                      <div className="pt-4">
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Quick setup</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Personal workspace</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Flexible pricing</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card>

                  {/* Organization Card */}
                  <Card
                    className="p-8 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 border-2 border-border group"
                    onClick={() => handleAccountTypeSelection("organization")}
                  >
                    <div className="space-y-6">
                      <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Building2 className="w-8 h-8 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-xl font-bold">Organization</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Perfect for teams and businesses. Manage multiple users, 
                          track usage, and collaborate seamlessly.
                        </p>
                      </div>
                      <div className="pt-4">
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Invite team members</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Centralized billing</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>Advanced analytics</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </Card>
          )}

          {step > 1 && accountType === "organization" && (
            <OrganizationOnboarding
              step={step - 1}
              onNext={() => setStep(step + 1)}
              onBack={handleBack}
              onComplete={handleComplete}
              apolloClient={apolloClient}
            />
          )}

          {step > 1 && accountType === "individual" && (
            <IndividualOnboarding
              step={step - 1}
              onNext={() => setStep(step + 1)}
              onBack={handleBack}
              onComplete={handleComplete}
              apolloClient={apolloClient}
            />
          )}
        </div>

        {/* Back Button (only on first step) */}
        {step === 1 && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
